<template>
  <o-sheet v-show="!loading" class="pa-6 overflow-hidden" :class="isLnr || isManager ? 'd-flex flex-column' : ''">
    <div class="d-flex">
      <div class="primary--text t-5 font-weight-bold">{{ getDiscussionTitle }}</div>
      <v-spacer />
      <o-btn class="text-subtitle-1" text @click="onViewAll">{{ $t(`captions.viewAll`) }}</o-btn>
    </div>

    <!-- Plan Image -->
    <div class="plan-img-container my-5" v-if="isLnr || isManager">
      <img v-if="planImg" :src="planImg" alt="plan-image" />
      <div v-else>
        <infinite-loading class="mt-8"> </infinite-loading>
      </div>
    </div>
    <div class="my-auto" :style="isManager || isLnr ? '' : 'height: 80% !important'">
      <div v-if="query.queryId || showManagerSessionStats" class="my-4" @click="mobileOnly ? show() : undefined">
        <upcoming-discussion-item
          v-if="!isManager"
          :query="query"
          :mobile="isMobile"
          :active="!isLnr"
          @respond="show"
        />
        <!-- learner stats card -->
        <v-row v-else class="manager-lnr-stats">
          <v-col cols="12" md="9" class="mx-auto">
            <p class="mb-0 primary--text text-center pa-8 content-container">
              {{
                $t("discussion.index.caption.sessionsSummary", {
                  totalQueries: queryStatsPlm.totalQueries,
                  uniqueLearnerCount: queryStatsPlm.uniqueLearnerCount,
                  learnerText: queryStatsPlm.uniqueLearnerCount === 1 ? "learner" : "different learners"
                })
              }}
            </p>
          </v-col>
        </v-row>
      </div>
      <div v-else v-show="!loading" class="fill-height">
        <div
          v-if="!isLnrOrManager"
          :class="{ 'grey--text text--darken-0': true, 'd-flex flex-wrap align-center justify-center': !isMobile }"
          style="height: 100% !important"
        >
          No discussion found
        </div>
        <o-sheet v-else class="primary--text resources-container py-5" :border="'primary'" :borderWidth="1">
          <p v-if="!isManager" class="font-weight-bold t-5">
            {{ noUpcomingTitle }}
          </p>
          <p class="mb-0">
            {{ supportText  }}
          </p>
        </o-sheet>
      </div>
    </div>
  </o-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import { MembershipService, PlanManagerQueryStatsItem, QueryIndexFullDisplay, QueryService } from "@/service";
import OSheet from "@/framework/OSheet.vue";
import OBtn from "@/framework/OBtn.vue";
import { i18n } from "@/plugins/i18n";

import UpcomingDiscussionItem from "./UpcomingDiscussionItem.vue";
import { UserType } from "@/config";

import { RootStoreInterface } from "@/store/types";
import { createHelpers } from "vuex-typescript-interface";
import { ThemeStoreInterface } from "@/store/theme/types";

// const { mapGetters } = createHelpers<RootStoreInterface>();
const { mapGetters: mapThemeGetters } = createHelpers<ThemeStoreInterface>();
const themeGetters = mapThemeGetters(["getCurrentTheme"]);
const { mapGetters } = createHelpers<RootStoreInterface>();

const getters = mapGetters(["activeUserType"]);

export default Vue.extend({
  components: {
    OSheet,
    OBtn,
    UpcomingDiscussionItem,
  },
  data: () => ({
    query: {} as QueryIndexFullDisplay,
    loading: false,
    planImg: "",
    queryStatsPlm: {} as PlanManagerQueryStatsItem,
    hasPastDiscussion: false,
  }),
  props: {
    userId: {
      type: [String, Number],
      required: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    mobileOnly: {
      type: Boolean,
      default: false,
    },
    isExpert: {
      type: Boolean,
      required: true,
    },
    isLnrOrManager: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...getters,
    ...themeGetters,
    isLnr(): boolean {
      return this.activeUserType == UserType.LEARNER;
    },
    isManager(): boolean {
      return this.activeUserType == UserType.PLAN_MANAGER;
    },
    appliedTheme() {
      const theme = this.getCurrentTheme?.name;
      switch (theme) {
        case "CEN":
        case "Onviv":
          return {
            logo: "/assets/themes/Onviv/fallbackLogo.png",
          };
        default:
          return {
            logo: "/assets/themes/Noetic/new-ni-onviv-logo-padding.png",
          };
      }
    },
    getDiscussionTitle(): string {
      return this.isLnr
        ? (i18n.t(`discussion.index.title.featured`) as string)
        : this.isManager
          ? (i18n.t(`discussion.index.title.training`) as string)
          : !this.mobileOnly
            ? (i18n.t(`discussion.index.title.featured`) as string)
            : "Featured";
    },
     hasCompletedSessions(): boolean {
      return (this.queryStatsPlm.totalCompletedSessions ?? 0) > 0;
    },
    showManagerSessionStats(): boolean {
      return this.isManager && this.hasCompletedSessions;
    },
    noUpcomingTitle(): string {
      if (!this.isLnr) return this.$t("discussion.index.caption.noUpcomingDiscussions") as string;

      return this.hasPastDiscussion
        ? (this.$t("discussion.index.caption.noUpcomingDiscussionsCurrently") as string)
        : (this.$t("discussion.index.caption.noUpcomingDiscussions") as string);
    },
    supportText(): string {
      if (!this.isLnr) {
        return this.$t("discussion.index.caption.supportTextPlm") as string;
      }

      return this.hasPastDiscussion
        ? (this.$t("discussion.index.caption.supportTextLnrContinue") as string)
        : (this.$t("discussion.index.caption.supportTextLnr") as string);
    },
    // planImgUrl(): string {
    //   return this.planImg || "/assets/themes/Noetic/fallbackLogo.png";
    // },
  },
  methods: {
    async getPlanImgUrl() {
      this.loading = true;
      let imgUrl = null;
      try {
        const r = await MembershipService.getMembershipPlanFiles({ id: Number(this.userId) });
        if (r && r.url) {
          imgUrl = r.url;
        }
      } catch (error) {
        console.error("Error fetching plan image:", error);
      } finally {
        this.loading = false;
      }
      return imgUrl || this.appliedTheme.logo;
    },

    upcomingDiscussions() {
      if (!Array.isArray(this.discussionList) || this.discussionList.length === 0) {
        return null;
      }

      const currentDate = new Date().getTime();

      const upcoming = this.discussionList
        .map((item) => ({
          ...item,
          meetingTime: item.meetingTimeDisplay ? new Date(item.meetingTimeDisplay).getTime() : null,
        }))
        .filter((item) => item.meetingTime && item.meetingTime > currentDate);

      if (!upcoming.length) return null;

      upcoming.sort((a, b) => a.meetingTime - b.meetingTime);
      return upcoming[0];
    },

    async load() {
      this.loading = true;
      try {
        const r = await QueryService.getQueryFeatured({ id: Number(this.userId) });
        if (r.data && r.data.length) {
          this.query = r.data[0];
        }
      } catch (e) {
        // TODO: Handle Error
        console.log(e);
      }
      this.loading = false;
    },
    async fetchPlmStats() {
      this.loading = true;
      try {
        const r = await QueryService.getQueryStatsPlanMgr({ id: Number(this.userId) });
        if (r) this.queryStatsPlm = r;
      } catch (e) {
        console.log("error in fetching manager stats", e);
      }
      this.loading = false;
    },
    onViewAll() {
      this.$router
        .push({ name: "Discussions", hash: this.isExpert ? "#requiresAction" : "#newOpportunities" })
        .catch(() => {
          /* Handle Error */
        });
    },
    show() {
      this.$router
        .push({ name: this.query.isInvited ? "Invitation" : "Discussion", params: { id: String(this.query.queryId) } })
        .catch(() => {
          /* Handle Error */
        });
    },
    async fetchHasPastDiscussion() {
      const now = Date.now();
      const payload = {
        pageSize: 1,
        sort: "meetingTime:desc",
        meetingEnd: now,
      };

      try {
        const r = await QueryService.getQueryIndex(payload);
        this.hasPastDiscussion = !!(r?.data && r.data.length);
      } catch (e) {
        console.log("error checking past discussions:", e);
        this.hasPastDiscussion = false;
      }
    },
  },
  async mounted() {
    this.isManager ? this.fetchPlmStats() : this.load();
    this.fetchHasPastDiscussion();
    this.planImg = await this.getPlanImgUrl();
  },
});
</script>
<style lang="scss" scoped>
.plan-img-container {
  width: 100%;
  text-align: center;
  margin: 20px 0;
  img {
    max-width: 100%;
    height: auto;
    max-height: 180px;
    object-fit: contain;
  }
}
@media (min-width: 960px) {
  .plan-img-container {
    height: 32%;

    img {
      height: 100%;
      width: 100%;
      max-height: none;
    }
  }
}

.manager-lnr-stats .content-container {
  border: 2px solid var(--v-primary-base);
  border-radius: 8px;
}
// .resources-container {
//   height: 98px;
// }
</style>
